import {
  ButtonCircle,
  Flex,
  IconNext,
  Text,
} from '@momentum-ui/react-collaboration';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useWebexClasses from '../../hooks/useWebexClasses';
import { formatDuration, formatDurationMmSs } from '../../utils/dateUtils';
import { ScrubbingBar } from './ScrubbingBar';
import { useVoicemailAudio } from './VoicemailAudioProvider';
import './VoicemailItem.styles.scss';
import './VoicemailPlaybackControls.styles.scss';

export interface IVoicemailPlaybackControlsProps {
  voicemailId: string; // Add voicemailId to identify which voicemail this controls
  audioSrc: string;
  onPlay?: () => void;
  className?: string;
  duration: number;
  audioSrcLoader?: boolean;
  focusPauseButton?: () => void;
  setIsAnnouncePlayOrPause: React.Dispatch<React.SetStateAction<boolean>>;
  voicemailName?: string;
  playButtonRef: React.RefObject<HTMLButtonElement>;
  pauseButtonRef: React.RefObject<HTMLButtonElement>;
  audioButtonRef: React.RefObject<HTMLButtonElement>;
  trackRef: React.RefObject<HTMLDivElement>;
  setAccordionExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  isSelected?: boolean;
  buttonSize?: 28 | 52;
  playIcon?: React.ReactNode;
  compactTime?: boolean;
}

export const VoicemailPlaybackControls = ({
  voicemailId, // Add voicemailId parameter
  audioSrc,
  onPlay = () => { },
  className = undefined,
  duration,
  audioSrcLoader = false,
  setIsAnnouncePlayOrPause,
  voicemailName,
  playButtonRef,
  pauseButtonRef,
  audioButtonRef,
  trackRef,
  isSelected,
  buttonSize = 28,
  playIcon,
  compactTime = false,
  setAccordionExpanded,
}: IVoicemailPlaybackControlsProps) => {
  const { t } = useTranslation('WebexVoicemail');
  const audioContext = useVoicemailAudio();
  
  // Get audio state for this specific voicemail. Destructure only what we need
  // to avoid re-running effects due to whole context object identity changes.
  const isCurrentVoicemail = audioContext.isCurrentVoicemail(voicemailId);
  const contextPlaying = audioContext.playing; // raw playing state from provider
  const playing = isCurrentVoicemail ? contextPlaying : false;
  const curTime = isCurrentVoicemail ? audioContext.currentTime : 0;
  const playVoicemailFn = audioContext.playVoicemail; // capture function reference

  // Tracks whether user initiated play while audio was not yet available
  const autoPlayRequestedRef = useRef(false);

  const playAudio = () => {
    onPlay();
    // If this is the current voicemail and it's just paused with a ready src, resume
    if (isCurrentVoicemail && audioContext.audioSrc && !audioContext.playing) {
      autoPlayRequestedRef.current = false; // we already have src
      audioContext.resumeVoicemail();
    } else {
      // If audio source not yet available (fetch in-flight), remember intent
      if (!audioSrc) {
        autoPlayRequestedRef.current = true;
      } else {
        autoPlayRequestedRef.current = false;
      }
      // Start (or queue) this voicemail. Provider will set current voicemail id even if src empty
      audioContext.playVoicemail(voicemailId, audioSrc, audioSrcLoader);
    }
    // Only expand accordion if not already selected
    if (!isSelected) {
      setAccordionExpanded(true);
    }
  };
  const [cssClasses, sc] = useWebexClasses('voicemail-playback-controls');

  useEffect(() => {
    if (playing && audioSrc) {
      setIsAnnouncePlayOrPause(true);
    } else {
      setIsAnnouncePlayOrPause(false);
    }
  }, [playing, audioSrc, setIsAnnouncePlayOrPause]);

  // Auto-play when the async audio src arrives after an initial user play intent
  useEffect(() => {
    // Auto-play when the async audio src arrives after an initial user play intent.
    // We intentionally depend only on the minimal set of stable values to avoid
    // spurious re-runs if the provider recreates the context object.
    if (
      autoPlayRequestedRef.current && // user clicked play earlier
      audioSrc && // src now available
      isCurrentVoicemail && // still the same voicemail focused
      !contextPlaying // not already playing
    ) {
      playVoicemailFn(voicemailId, audioSrc, audioSrcLoader);
      autoPlayRequestedRef.current = false; // consume intent
    }
  }, [audioSrc, isCurrentVoicemail, contextPlaying, voicemailId, audioSrcLoader, playVoicemailFn]);

  // Periodic tick debug (throttle by rounding time to 0.5s)
  const rounded = Math.floor(curTime * 2) / 2;
  const lastLoggedRef = useRef<number | null>(null);
  if (isCurrentVoicemail && rounded !== lastLoggedRef.current) {
    lastLoggedRef.current = rounded;
  }

  return (
    <Flex xgap=".5rem" alignItems="center" className={`${className} ${cssClasses}`}>
      {playing && audioSrc ? (
        <ButtonCircle
          outline
          ghost
          size={buttonSize}
          onPress={() => {
            autoPlayRequestedRef.current = false;
            audioContext.pauseVoicemail();
          }}
          data-testid="pause-button"
          title={t('pauseVoicemail')}
          ref={pauseButtonRef}
          aria-label={t('pauseVoicemail')}
        >
          <IconNext name="pause" autoScale={150} />
        </ButtonCircle>
      ) : (
        <ButtonCircle
          outline
          ghost
          size={buttonSize}
          onPress={playAudio}
          data-testid="play-button"
          title={t('playVoicemail')}
          ref={playButtonRef}
          aria-label={t('playVoicemail')}
        >
          {(audioSrcLoader && audioSrc === '') ? (
            <div className='vmLoader'>
              <div className="md-loading-spinner-wrapper" data-testid="loading-icon">
                <div className="md-icon-wrapper">
                  <svg xmlns="http://www.w3.org/2000/svg"
                    width="100%" height="100%" data-test="spinner" fill="currentColor" viewBox="0, 0, 32, 32" data-scale="28" data-autoscale="false">
                    <path d="M16 2.25A13.75 13.75 0 1 0 29.75 16 13.765 13.765 0 0 0 16 2.25Zm0 26A12.25 12.25 0 1 1 28.25 16 12.263 12.263 0 0 1 16 28.25Z">
                    </path>
                  </svg>
                </div>
                <div className="md-icon-wrapper md-loading-spinner-arch">
                  <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" data-test="spinner-partial" fill="currentColor" viewBox="0, 0, 32, 32" data-scale="28" data-autoscale="false">
                    <path d="M15.25 3a.75.75 0 0 0 .75.75A12.264 12.264 0 0 1 28.25 16a.75.75 0 1 0 1.5 0A13.765 13.765 0 0 0 16 2.25a.75.75 0 0 0-.75.75Z">
                    </path>
                  </svg>
                </div>
              </div>
            </div>
          ) : (
            playIcon || (
              <IconNext name="play" autoScale={150} className={sc('play-icon')} />
            )
          )}
        </ButtonCircle>
      )}

      <Text className={sc('current-time')}>
        {compactTime ? formatDurationMmSs(curTime) : formatDuration(curTime)}
      </Text>
      <ScrubbingBar
        maxValue={duration / 1000}
        value={[curTime]}
        onChange={([value]) => audioContext.setCurrentTime(value)}
        step={Math.min(duration / 1000, 0.1)}
        voicemailName={voicemailName}
        duration={duration}
        playButtonRef={playButtonRef}
        pauseButtonRef={pauseButtonRef}
        audioButtonRef={audioButtonRef}
        trackRef={trackRef}
      />
      <Text data-testid="total-duration">
        {compactTime
          ? formatDurationMmSs(duration / 1000)
          : formatDuration(duration / 1000)}
      </Text>
    </Flex>
  );
};
